// @ts-strict-ignore
import List from 'antd/lib/list';
import Collapse from 'antd/lib/collapse';
import * as React from 'react';
import { useSelector } from 'react-redux';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectNotifications,
    selectStepConfigDataFromStepGraphs,
    selectWorkflowId,
} from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { AppComponentProps } from '../../typescriptHelpers';
import styles from '../styles/settingsStyles';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import NotificationSettingsModal from './NotificationSettingsModal';
import NotificationStepConfigOverrideRow from './NotificationStepConfigOverrideRow';
import Search from 'antd/lib/input/Search';
import NotificationRow from '../common/NotificationRow';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { createUserSettingsRoute } from '@/helpers/routeHelpers';
import { UserSettingsPageView } from '@/components/userSettings/UserSettingsComponent';
import { SortTableButton } from '@/components/tableFilters/SortTableButton';
import { SettingsSortType } from '@/types/sorts';
import { AppNotificationType, StepNotificationConfigOverride } from '../../types';
import { CaretRightOutlined } from '@ant-design/icons';
import { antGrey } from '../styles/globalStyles';
import { StepGraphStep } from '@/types/workflow';

interface AppNotificationsProps extends AppComponentProps {
    refProp?: React.MutableRefObject<HTMLDivElement>;
}

const AppNotifications: React.FC<AppNotificationsProps> = (props: AppNotificationsProps) => {
    const { appId, orgId } = props;
    const notifications = useSelector((state: RootState) => selectNotifications(state, appId));
    const stepGraphStepsMap = useSelector((state: RootState) => selectStepConfigDataFromStepGraphs(state, appId));
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    const [showNotificationSettings, setShowNotificationSettings] = React.useState(false);
    const [notificationIdSelectedForSettings, setNotificationIdSelectedForSettings] = React.useState(null);
    const [searchValue, setSearchValue] = React.useState('');
    const [selectedSortType, setSelectedSortType] = React.useState<SettingsSortType>(SettingsSortType.Name);

    // State for step override modal
    const [stepOverrideModal, setStepOverrideModal] = React.useState<{
        notificationId: AppNotificationType;
        stepConfigId: string;
        stepConfigName: string;
        override: StepNotificationConfigOverride | null;
    } | null>(null);

    const history = useHistory();
    const location = useLocation();

    const stripNotificationDeepLinkParamsFromUrl = () => {
        const params = new URLSearchParams(location.search);
        if (!params.has('notificationId') && !params.has('stepConfigId')) {
            return;
        }
        params.delete('notificationId');
        params.delete('stepConfigId');
        const newSearch = params.toString();
        history.replace({
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : '',
            hash: location.hash,
            state: location.state,
        });
    };

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const idFromQuery = params.get('notificationId');
        if (idFromQuery) {
            const stepConfigIdFromQuery = params.get('stepConfigId');
            if (stepConfigIdFromQuery && stepGraphStepsMap) {
                // Deep link to a step override modal
                const stepGraphStep = stepGraphStepsMap[stepConfigIdFromQuery];
                if (stepGraphStep) {
                    const notification = notifications.find((n) => n.id === idFromQuery);
                    const override =
                        notification?.stepConfigOverrides?.find((o) => o.stepConfigId === stepConfigIdFromQuery) ??
                        null;
                    setStepOverrideModal({
                        notificationId: idFromQuery as AppNotificationType,
                        stepConfigId: stepConfigIdFromQuery,
                        stepConfigName: stepGraphStep.stepName,
                        override,
                    });
                    return;
                }
            }
            // Default: open app-level notification settings
            setNotificationIdSelectedForSettings(idFromQuery);
            setShowNotificationSettings(true);
        }
    }, [location.search, stepGraphStepsMap, notifications]);

    const processedNotifications = React.useMemo(() => {
        const filtered = notifications.filter((notif) =>
            notif.displayText.toLowerCase().includes(searchValue.toLowerCase()),
        );

        return [...filtered].sort((a, b) => {
            switch (selectedSortType) {
                case SettingsSortType.Name:
                    return a.displayText.localeCompare(b.displayText);

                case SettingsSortType.EnabledFirst:
                    if (a.enabled === b.enabled) return a.displayText.localeCompare(b.displayText);
                    return a.enabled ? -1 : 1;

                default:
                    return 0;
            }
        });
    }, [notifications, searchValue, selectedSortType]);

    // Get matching step configs for a notification based on its applicable archetypes
    const getMatchingStepConfigs = React.useCallback(
        (applicableArchetypes: string[]): StepGraphStep[] => {
            if (!stepGraphStepsMap || !applicableArchetypes?.length) return [];
            return Object.values(stepGraphStepsMap).filter((step) => applicableArchetypes.includes(step.archetype));
        },
        [stepGraphStepsMap],
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} ref={props.refProp}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Space size={8}>
                    <Text style={styles.appSettingsSectionTitleStyle}>Notifications</Text>
                </Space>
                <Space>
                    <SortTableButton<SettingsSortType>
                        selectedSortType={selectedSortType}
                        sortOptions={[
                            {
                                sortType: SettingsSortType.Name,
                                text: 'Name',
                            },
                            {
                                sortType: SettingsSortType.EnabledFirst,
                                text: 'Enabled first',
                            },
                        ]}
                        onSelectSort={(sortType) => {
                            setSelectedSortType(sortType);
                        }}
                    />

                    <Search
                        placeholder="Search notifications"
                        allowClear
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onSearch={(value) => {
                            setSearchValue(value);
                        }}
                        style={{ width: 400 }}
                    />
                </Space>
            </div>
            <List
                header={null}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 10, size: 'small', showSizeChanger: false }}
                dataSource={processedNotifications}
                renderItem={(notification) => {
                    const matchingStepConfigs = getMatchingStepConfigs(notification.applicableStepArchetypes);
                    const hasStepConfigs = matchingStepConfigs.length > 1;

                    const notificationRow = (
                        <NotificationRow
                            notification={notification}
                            notificationsEnabled
                            onClickSettings={(notificationId) => {
                                setNotificationIdSelectedForSettings(notificationId);
                                setShowNotificationSettings(true);
                            }}
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                        />
                    );

                    if (!hasStepConfigs) {
                        return <List.Item key={notification.id}>{notificationRow}</List.Item>;
                    }

                    return (
                        <List.Item key={notification.id}>
                            <Collapse
                                className="automations-collapsible"
                                defaultActiveKey={[]}
                                style={{ flex: 1 }}
                                ghost
                                expandIcon={({ isActive }) => (
                                    <CaretRightOutlined
                                        style={{
                                            color: antGrey.grey6,
                                            fontSize: 14,
                                        }}
                                        rotate={isActive ? 90 : 0}
                                    />
                                )}
                            >
                                <Collapse.Panel header={notificationRow} key="1">
                                    <List
                                        bordered
                                        size="small"
                                        dataSource={matchingStepConfigs}
                                        renderItem={(stepGraphStep) => {
                                            const override =
                                                notification.stepConfigOverrides?.find(
                                                    (o) => o.stepConfigId === stepGraphStep.stepConfigId,
                                                ) ?? null;

                                            return (
                                                <List.Item key={stepGraphStep.stepConfigId}>
                                                    <NotificationStepConfigOverrideRow
                                                        appId={appId}
                                                        orgId={orgId}
                                                        workflowId={workflowId}
                                                        notificationId={notification.id as AppNotificationType}
                                                        stepGraphStep={stepGraphStep}
                                                        override={override}
                                                        platform={platform}
                                                        appStoreType={appStoreType}
                                                        onClickSettings={() => {
                                                            setStepOverrideModal({
                                                                notificationId: notification.id as AppNotificationType,
                                                                stepConfigId: stepGraphStep.stepConfigId,
                                                                stepConfigName: stepGraphStep.stepName,
                                                                override,
                                                            });
                                                        }}
                                                    />
                                                </List.Item>
                                            );
                                        }}
                                    />
                                </Collapse.Panel>
                            </Collapse>
                        </List.Item>
                    );
                }}
            ></List>
            <Text>
                You can enable personal email notifications for any app-level notification from{' '}
                <Link to={createUserSettingsRoute(UserSettingsPageView.Email)}>
                    User settings &gt; Email notifications
                </Link>
                .
            </Text>
            <Text>
                Certain notifications also support email notifications to any address you specify in their respective
                notification settings.
            </Text>
            {notificationIdSelectedForSettings && (
                <NotificationSettingsModal
                    appId={props.appId}
                    orgId={props.orgId}
                    onClose={() => {
                        stripNotificationDeepLinkParamsFromUrl();
                        setShowNotificationSettings(false);
                    }}
                    open={showNotificationSettings}
                    notificationId={notificationIdSelectedForSettings}
                    onAfterClose={() => {
                        setNotificationIdSelectedForSettings(null);
                    }}
                />
            )}
            {stepOverrideModal && workflowId && (
                <NotificationSettingsModal
                    appId={appId}
                    orgId={orgId}
                    workflowId={workflowId}
                    notificationId={stepOverrideModal.notificationId}
                    stepConfigId={stepOverrideModal.stepConfigId}
                    stepConfigName={stepOverrideModal.stepConfigName}
                    override={stepOverrideModal.override}
                    open={true}
                    onClose={() => {
                        stripNotificationDeepLinkParamsFromUrl();
                        setStepOverrideModal(null);
                    }}
                    onAfterClose={() => setStepOverrideModal(null)}
                />
            )}
        </div>
    );
};

export default AppNotifications;
